import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import useCloseOnScroll from '../../../../hooks/useCloseOnScroll';

import { Radio, Dropdown, DatePicker, Tooltip } from 'antd';

import dayjs from 'dayjs';

import Icon from '../../atoms/global/Icon';
import { getDateColor } from '../../../../js/functions';
import {
  makeGetRecurringDuration,
  makeGetTaskDueDate,
  makeIsRecurringTask,
  makeGetTaskIsCompleted,
} from '../../../../redux/tasks/taskEntities/selectors';
import {
  editTaskAction,
  setRecurringTaskAction,
} from '../../../../redux/tasks/actions';
import { FormattedMessage, useIntl } from 'react-intl';
import { getLocalizedShortDate } from '../../../../_locale/dateLocales';

const CALENDAR_OPTIONS = ['day', 'week', 'month', 'year'];

const defaultFn = () => {};

const ConnectedCalendarDropdown = ({
  isLarge,
  onClick,
  showIcon = true,
  isRecurring,
  recurringDuration,
  dueDate: dueDateProp,
  dueDateRedux,
  taskId,
  isCompleted,
  shortMonth,
  readonly,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  // use dueDateProp given by the parent component or fallback to redux
  const dueDate = dueDateProp || dueDateRedux;

  const [calendarDate, setCalendarDate] = useState();
  const [showRecurringDate, setShowRecurringDate] = useState(
    isRecurring || false,
  );
  const [frequency, setFrequency] = useState(recurringDuration?.unit || 'day');
  const [duration, setDuration] = useState(recurringDuration?.duration || 1);
  const [iteration, setIteration] = useState(0);
  const [isTriggerComplete, setTriggerComplete] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const triggerRef = useRef(null);

  useCloseOnScroll(pickerOpen, triggerRef, () => setPickerOpen(false));

  useEffect(() => {
    if (dueDate) {
      const formattedDate = dayjs(dueDate).format('YYYY-MM-DD');
      setCalendarDate(dayjs(formattedDate, 'YYYY-MM-DD'));
    } else {
      setCalendarDate(null);
    }
  }, [dueDate]);

  const onChange = useCallback(
    (date, dateString) => {
      const dueDate = dayjs(date, 'YYYY-MM-DD');
      if (calendarDate == dueDate) {
        return;
      }

      if (onClick) {
        onClick(date);
        return;
      }

      setCalendarDate(dueDate);

      dispatch(
        editTaskAction({
          _id: taskId,
          dueDate: dueDate,
          recurringConfig: {
            recurring: showRecurringDate || false,
          },
        }),
      );
    },
    [onClick, showRecurringDate, taskId],
  );

  const durationItems = useMemo(() => {
    let items = [];
    for (let i = 1; i <= 7; i++) {
      items.push({
        key: i,
        label: i,
        onClick: () => setDuration(i),
      });
    }
    return items;
  }, []);

  const handleRecurringTask = () => {
    dispatch(
      setRecurringTaskAction({
        calendarDate,
        duration,
        frequency,
        isTriggerComplete,
        iteration,
        taskId,
      }),
    );
  };

  const dateColor = useMemo(
    () => getDateColor(calendarDate, isCompleted),
    [calendarDate, isCompleted],
  );

  const calendarIcon = useMemo(() => {
    const title = isRecurring ? 'Recurring' : 'Date';
    return (
      <Tooltip title={title}>
        {isRecurring ? (
          <img
            src={`/images/icons/Refresh.svg`}
            alt=""
            style={{
              height: '16px',
              width: '16px',
            }}
          />
        ) : (
          <svg
            className={`w-3.5 h-3.5 shrink-0 fill-gray-300`}
            viewBox="0 0 16 16"
          >
            <path
              d="M15 2h-2V0h-2v2H9V0H7v2H5V0H3v2H1a1 1 0 00-1 1v12a1 1 0 001 1h14a1 1 0 001-1V3a1 1 0 00-1-1zm-1 12H2V6h12v8z"
              fill={dateColor}
            />
          </svg>
        )}
      </Tooltip>
    );
  }, [dateColor, isRecurring]);

  const datePickerStyles = useMemo(
    () => ({
      border: `1px solid ${!isLarge || !showIcon ? 'transparent' : dateColor}`,
      color: showIcon ? dateColor : '#002337',
    }),
    [dateColor, isLarge, showIcon],
  );

  const panelRenderer = (Nodeee) => {
    if (!showRecurringDate) {
      return Nodeee;
    }

    return (
      <div className="w-[15vw] p-3 flex flex-col justify-between">
        <div className="flex align-center">
          <Icon
            name="close.svg"
            height="12px"
            onClick={() => {
              setShowRecurringDate(false);
            }}
          />
          <div className="font-14 text-slate-400 ml-4">
            {isRecurring ? 'EDIT' : ''} RECURRING TASK
          </div>
        </div>
        <div className="mt-4">
          <div className="font-14 mb-2">Repeat every</div>
          <div className="flex align-center">
            {frequency !== 'year' && (
              <Dropdown
                className="mr-2"
                overlayStyle={{ zIndex: 10000 }}
                trigger={['click']}
                menu={{
                  items: durationItems,
                }}
                placement="bottom"
                arrow
              >
                <div className="flex align-center justify-center border border-slate-200 p-1 px-4">
                  <div className="font-14">{duration}</div>
                  <img
                    src={'/images/icons/show-more.svg'}
                    style={{ margin: '10px' }}
                    alt="icon"
                  />
                </div>
              </Dropdown>
            )}
            <Dropdown
              overlayStyle={{ zIndex: 10000 }}
              trigger={['click']}
              menu={{
                items: CALENDAR_OPTIONS?.map((option) => ({
                  key: option,
                  label: option,
                  onClick: () => setFrequency(option),
                })),
              }}
              placement="bottom"
              arrow
            >
              <div className="flex align-center justify-center border border-slate-200 p-1 px-5">
                <div className="font-14">
                  {frequency}
                  {duration > 1 && 's'}
                </div>
                <img
                  src={'/images/icons/show-more.svg'}
                  style={{ margin: '10px' }}
                  alt="icon"
                />
              </div>
            </Dropdown>
          </div>
        </div>
        <div className="flex flex-col mt-4 font-16">
          <div className="font-14 mb-2">Create new tasks</div>
          <Radio
            className="mb-2"
            checked={isTriggerComplete}
            onChange={() => setTriggerComplete(true)}
          >
            Upon Completion
          </Radio>
          <Radio
            checked={!isTriggerComplete}
            onChange={() => setTriggerComplete(false)}
          >
            Now
          </Radio>
        </div>

        <div className="my-5 flex flex-col justify-center">
          <div className="flex align-center mb-2">
            <div className="font-14 mr-5">Ends</div>
          </div>
          <Radio
            checked={isTriggerComplete}
            onChange={() => setTriggerComplete(true)}
          >
            Never
          </Radio>
          {/* <div className='flex align-center justify-between'>
          <Radio checked={!isTriggerComplete} onChange={() => setTriggerComplete(false)}>On</Radio>
          <DatePicker />
        </div> */}
          <div className="flex align-center justify-between">
            <Radio
              checked={!isTriggerComplete}
              onChange={() => setTriggerComplete(false)}
            >
              After
            </Radio>
            <div className="ml-2 flex align-center">
              <input
                max={99}
                className="h-8 w-12 px-1 border-slate-400 border"
                type="number"
                value={iteration}
                onChange={(e) => setIteration(e?.target?.value)}
              />
              <div className="font-14 ml-5">Occurances</div>
            </div>
          </div>
        </div>
        <div className="flex flex-col align-center justify-around">
          <div
            onClick={() => setShowRecurringDate(false)}
            className="cursor-pointer hover:text-blue-500 p-2 border-t-2 w-full text-center border-slate-100"
          >
            {isRecurring ? 'Set Date' : 'Cancel'}
          </div>
          <div
            onClick={() => {
              setShowRecurringDate(false);
              handleRecurringTask();
            }}
            className="cursor-pointer hover:text-blue-500 p-2 border-t-2 w-full text-center border-slate-100"
          >
            {isRecurring ? 'Update' : 'Save'}
          </div>
        </div>
      </div>
    );
  };

  return (
    <span ref={triggerRef}>
    <DatePicker
      size={isLarge ? 'middle' : 'small'}
      placeholder={readonly ? '--' : 'Set Date'}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      style={datePickerStyles}
      format={(date) => getLocalizedShortDate(date, intl, shortMonth)}
      suffixIcon={showIcon && (!dueDate || isLarge) ? calendarIcon : false}
      value={calendarDate}
      allowClear={false}
      showToday={false}
      open={pickerOpen}
      onOpenChange={setPickerOpen}
      panelRender={readonly ? defaultFn : panelRenderer}
      renderExtraFooter={() => (
        <div className="flex justify-between items-center px-3">
          <div
            onClick={() => onChange(dayjs())}
            className="cursor-pointer text-[#0473cc] hover:underline"
          >
            <FormattedMessage id="TODAY" defaultMessage="Today" />
          </div>
          <div
            onClick={() => {
              dispatch(
                editTaskAction({
                  _id: taskId,
                  dueDate: null,
                  recurringConfig: {
                    recurring: showRecurringDate || false,
                  },
                }),
              );
            }}
            className="cursor-pointer text-red-600 hover:underline"
          >
            <FormattedMessage id="CLEAR" defaultMessage="Clear" />
          </div>
        </div>
      )}
    />
    </span>
  );
};

const makeMapStateToProps = () => {
  const getTaskDueDate = makeGetTaskDueDate();
  const getRecurringDuration = makeGetRecurringDuration();
  const isRecurringTask = makeIsRecurringTask();
  const getTaskIsCompleted = makeGetTaskIsCompleted();
  return (state, props) =>
    props?.readonly
      ? { dueDateRedux: props?.dueDate }
      : {
          isRecurring: isRecurringTask(state, props),
          recurringDuration: getRecurringDuration(state, props),
          dueDateRedux: getTaskDueDate(state, props),
          isCompleted: getTaskIsCompleted(state, props),
        };
};

export default connect(makeMapStateToProps)(ConnectedCalendarDropdown);
